import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import { IBlogCategory, IBlogListItem } from '@/redux/blog/blog.d';

function formatPublishedAt(date: string | null | undefined): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'MMM d, yyyy');
}

function CardCover({
  coverImageUrl,
  title,
}: {
  coverImageUrl: string | null;
  title: string;
}) {
  if (!coverImageUrl) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
    );
  }
  return (
    <img
      src={coverImageUrl}
      alt={title}
      className="w-full h-full object-contain"
      loading="lazy"
      decoding="async"
    />
  );
}

function CategoryChip({ category }: { category: IBlogCategory | undefined }) {
  if (!category) return null;
  return (
    <span className="absolute top-3 left-3 rounded-full bg-primary/90 px-3 py-1 text-xs font-medium text-white capitalize">
      {category.name}
    </span>
  );
}

function CardExcerpt({ excerpt }: { excerpt: string }) {
  if (!excerpt) return null;
  return (
    <p className="text-sm line-clamp-3 font-tertiary font-normal text-gray-600">
      {excerpt}
    </p>
  );
}

function DateText({ formattedDate }: { formattedDate: string }) {
  if (!formattedDate) return null;
  return <span>{formattedDate}</span>;
}

function MetaSeparator({ show }: { show: boolean }) {
  if (!show) return null;
  return <span className="text-gray-300">·</span>;
}

function ReadTime({ readTimeMinutes }: { readTimeMinutes: number }) {
  if (!readTimeMinutes) return null;
  return (
    <span className="flex items-center gap-1">
      <Clock className="h-3.5 w-3.5" />
      {readTimeMinutes} min read
    </span>
  );
}

function CardMeta({
  formattedDate,
  readTimeMinutes,
}: {
  formattedDate: string;
  readTimeMinutes: number;
}) {
  const showSeparator = Boolean(formattedDate) && Boolean(readTimeMinutes);
  return (
    <div className="flex items-center gap-3 text-xs text-[#666666] font-secondary">
      <DateText formattedDate={formattedDate} />
      <MetaSeparator show={showSeparator} />
      <ReadTime readTimeMinutes={readTimeMinutes} />
    </div>
  );
}

function BlogCard({ post }: { post: IBlogListItem }) {
  const { slug, title, excerpt, coverImageUrl, categories } = post;
  const primaryCategory = categories?.[0];
  const formattedDate = formatPublishedAt(post.publishedAt);

  return (
    <Card className="w-full h-full min-w-0 flex flex-col bg-white border-[1.5px] border-gray-400 overflow-hidden">
      <Link to={`/blog/${slug}`} className="flex flex-col flex-1">
        <div className="w-full h-[192px] flex items-center justify-center bg-gray-50 relative overflow-hidden">
          <CardCover coverImageUrl={coverImageUrl} title={title} />
          <CategoryChip category={primaryCategory} />
        </div>
        <div className="flex flex-col justify-between gap-3 p-5 flex-1">
          <div className="flex flex-col gap-2">
            <h5 className="text-lg font-medium leading-snug font-primary line-clamp-2">
              {title}
            </h5>
            <CardExcerpt excerpt={excerpt} />
          </div>
          <CardMeta
            formattedDate={formattedDate}
            readTimeMinutes={post.readTimeMinutes}
          />
        </div>
      </Link>
    </Card>
  );
}

export default BlogCard;
